<script setup>
import { computed, ref } from "vue";
import Swal from "sweetalert2";
import { GST_TYPES } from "./companySettingsRules";
import { useCompanySettings } from "./useCompanySettings";

const emit = defineEmits(["go"]);
const { form, canEdit, logoBusy, progress, uploadLogo, removeLogo } = useCompanySettings();

const fileInput = ref(null);
const ALLOWED_TYPES = ["image/png", "image/jpeg", "image/jpg", "image/webp"];
const MAX_LOGO_BYTES = 2 * 1024 * 1024;

const initials = computed(() =>
  (form.DisplayName || form.CompanyName || "?")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0].toUpperCase())
    .join("")
);
const gstLabel = computed(() => GST_TYPES.find((type) => type.value === form.GSTType)?.label || form.GSTType);
const place = computed(() => [form.City, form.State].filter(Boolean).join(", "));

function pickLogo() {
  if (canEdit.value && !logoBusy.value) fileInput.value?.click();
}

async function onLogoSelected(event) {
  const file = event.target.files?.[0];
  event.target.value = "";
  if (!file) return;
  if (!ALLOWED_TYPES.includes(file.type)) {
    Swal.fire("Unsupported image", "Use a PNG, JPG or WEBP image.", "warning");
    return;
  }
  if (file.size > MAX_LOGO_BYTES) {
    Swal.fire("Image too large", "The logo must be 2 MB or smaller.", "warning");
    return;
  }
  try {
    await uploadLogo(file);
  } catch (error) {
    Swal.fire("Upload failed", error?.response?.data?.detail || "Could not upload the logo.", "error");
  }
}

async function onRemoveLogo() {
  const result = await Swal.fire({ title: "Remove the logo?", icon: "warning", showCancelButton: true, confirmButtonText: "Remove" });
  if (!result.isConfirmed) return;
  try {
    await removeLogo();
  } catch (error) {
    Swal.fire("Error", error?.response?.data?.detail || "Could not remove the logo.", "error");
  }
}
</script>

<template>
  <div class="cs-hero">
    <div class="cs-hero-main">
      <div class="cs-logo" :class="{ editable: canEdit, busy: logoBusy }">
        <button type="button" class="cs-logo-button" :disabled="!canEdit || logoBusy" :title="canEdit ? 'Change logo' : ''" @click="pickLogo">
          <img v-if="form.LogoUrl" :src="form.LogoUrl" alt="Company logo" />
          <span v-else class="cs-logo-initials">{{ initials }}</span>
          <span v-if="canEdit" class="cs-logo-overlay">
            <vue-feather :type="logoBusy ? 'loader' : 'camera'" size="18"></vue-feather>
          </span>
        </button>
        <input ref="fileInput" type="file" class="d-none" accept="image/png,image/jpeg,image/webp" @change="onLogoSelected" />
      </div>

      <div class="cs-hero-text">
        <h4>{{ form.DisplayName || form.CompanyName || "Your company" }}</h4>
        <div class="cs-hero-meta">
          <span v-if="form.DisplayName && form.CompanyName && form.DisplayName !== form.CompanyName">{{ form.CompanyName }}</span>
          <span v-if="place"><vue-feather type="map-pin" size="13"></vue-feather> {{ place }}</span>
          <span class="cs-badge">{{ gstLabel }}<template v-if="form.GSTIN"> · {{ form.GSTIN }}</template></span>
        </div>
        <div v-if="canEdit" class="cs-logo-actions">
          <button type="button" class="btn btn-link btn-sm p-0" :disabled="logoBusy" @click="pickLogo">
            {{ form.LogoUrl ? "Change logo" : "Upload logo" }}
          </button>
          <template v-if="form.LogoUrl">
            <span class="text-muted">·</span>
            <button type="button" class="btn btn-link btn-sm p-0 text-danger" :disabled="logoBusy" @click="onRemoveLogo">Remove</button>
          </template>
          <small class="text-muted ms-2">PNG, JPG or WEBP, up to 2 MB</small>
        </div>
      </div>
    </div>

    <div class="cs-progress">
      <a-progress type="circle" :percent="progress.percent" :width="64" :stroke-color="progress.percent === 100 ? '#16a34a' : '#fb923c'" />
      <div>
        <div class="fw-semibold">{{ progress.percent === 100 ? "Setup complete" : "Complete your setup" }}</div>
        <ul v-if="progress.missing.length || !progress.requiredComplete" class="cs-checklist">
          <li v-if="!progress.requiredComplete">
            <a href="javascript:void(0)" @click="emit('go', 'business')">Fill the required details</a>
          </li>
          <li v-for="item in progress.missing.slice(0, 3)" :key="item.field">
            <a href="javascript:void(0)" @click="item.field === 'LogoUrl' ? pickLogo() : emit('go', item.section, item.field)">
              {{ item.label }}
            </a>
          </li>
          <li v-if="progress.missing.length > 3" class="text-muted">+{{ progress.missing.length - 3 }} more</li>
        </ul>
      </div>
    </div>
  </div>
</template>
